import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// components and helpers
import MappingPreview from './MappingPreview';
import ImportProgressPreview from './ImportProgressPreview';
import DataMapping from './DataMapping';
import AssetUpload from './AssetUpload';
import { workspaceActions } from '../../WorkspaceActions';

// mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function UploadFileDialog({ open, handleClose }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const [activeStep, setActiveStep] = useState(0);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rowOne, setRowOne] = useState<string[]>([]);
    const [rowTwo, setRowTwo] = useState<string[]>([]);
    const [fileToImport, setFileToImport] = useState({});
    const initiatedFileImport = useSelector(
        (state: any) => state.workspace.initiatedFileImport
    );
    const savedMapping = useSelector(
        (state: any) => state.workspace.savedMapping
    );
    const [mappedFields, setMappedFields] = useState({});
    const [fileType, setFileType] = useState<string>('');
    const [extension, setExtension] = useState('');
    const [filesAdded, setFilesAdded] = useState(null);
    const intervalFileImportDialog = useRef();

    useEffect(() => {
        if (
            activeStep === 1 &&
            savedMapping &&
            savedMapping.mapping &&
            (savedMapping.mapping.case_id || savedMapping.mapping.case_id === 0)
        ) {
            setMappedFields(savedMapping.mapping);
        }
    }, [activeStep, savedMapping]);

    const handleNext = () => {
        const id = location.pathname.split('/').pop();
        if (activeStep === 1) {
            // save mapping to file import id
            dispatch(
                workspaceActions.saveFileImportMapping(
                    initiatedFileImport.id,
                    mappedFields
                )
            );
            // save mapping configuration of file type to workspace id for future application
            dispatch(
                workspaceActions.setWorkspaceConfiguration(
                    id,
                    `${fileType.toLowerCase()}-configuration`,
                    mappedFields
                )
            );
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleCloseDialog = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleReset();
    };

    const handleCancel = () => {
        if (activeStep === 0) {
            handleReset();
        } else {
            dispatch(
                workspaceActions.deleteDraftImport(initiatedFileImport.id)
            );
            handleReset();
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setHeaders([]);
        setMappedFields({});
        setRowOne([]);
        setRowTwo([]);
        setFileToImport({});
        setFileType('');
        setExtension('');
        setFilesAdded(null);
        dispatch(workspaceActions?.resetFileImportStatus());
        clearInterval(intervalFileImportDialog.current);
        intervalFileImportDialog.current = null;
        handleClose();
    };

    const handleContinueProcess = () => {
        const workspaceId = location.pathname.split('/').pop();
        dispatch(workspaceActions.initiateFileImport(workspaceId));
        handleMapping(filesAdded);
    };

    const handleStartImport = (
        e,
        extension?: string,
        file?: any,
        id?: string
    ) => {
        dispatch(
            workspaceActions.uploadFile(
                id ? id : initiatedFileImport.id,
                file ? file : fileToImport,
                fileType
            )
        );
        dispatch(
            workspaceActions.getFileImportProgress([
                id ? id : initiatedFileImport.id,
            ])
        );
        if (!extension) {
            handleNext();
        } else {
            setActiveStep(steps.length);
        }
    };

    const handleMapping = async (data: any) => {
        setActiveStep(activeStep + 1);
        const file = await data.data.arrayBuffer();
        setFileToImport(data);
        const workbook = XLSX.read(file, {
            sheetRows: 3,
            dense: true,
            type: 'binary',
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw_data = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: false,
            defval: '',
            blankrows: true,
        });
        setHeaders(raw_data[0] as string[]);
        setRowOne((raw_data[1] as string[]) || []);
        setRowTwo((raw_data[2] as string[]) || []);
    };

    const steps = [
        'Upload your data',
        'Complete field mapping',
        'Review and confirm!',
    ];

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCloseDialog}
            aria-describedby="alert-dialog-slide-description"
            fullWidth
            maxWidth={'lg'}
            disableEscapeKeyDown
        >
            <DialogTitle>{'Data Import Wizard'}</DialogTitle>
            <Box sx={{ width: '100%', p: 2 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {steps.map((label, index) => {
                        const stepProps: { completed?: boolean } = {};
                        const labelProps: {
                            optional?: React.ReactNode;
                        } = {};
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                {activeStep === steps.length ? (
                    <React.Fragment>
                        <ImportProgressPreview />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                pt: 2,
                            }}
                        >
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button onClick={handleReset}>Done</Button>
                        </Box>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {activeStep === 0 && (
                            <AssetUpload
                                fileType={fileType}
                                setFileType={setFileType}
                                setExtension={setExtension}
                                setFilesAdded={setFilesAdded}
                            />
                        )}
                        {activeStep === 1 && (
                            <DataMapping
                                headers={headers}
                                mappedFields={mappedFields}
                                setMappedFields={setMappedFields}
                                fileType={fileType}
                            />
                        )}
                        {activeStep === 2 && (
                            <MappingPreview
                                rows={[rowOne, rowTwo]}
                                headers={headers}
                                mappedFields={mappedFields}
                            />
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                pt: 2,
                            }}
                        >
                            <Button
                                color={'error'}
                                variant={'outlined'}
                                onClick={handleCancel}
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ flex: '1 1 auto' }} />
                            {activeStep > 1 && (
                                <Button
                                    color="primary"
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    sx={{ mr: 1 }}
                                    variant={'outlined'}
                                >
                                    Back
                                </Button>
                            )}
                            {activeStep === 0 &&
                                extension &&
                                filesAdded &&
                                Object.entries(filesAdded).length && (
                                    <Button
                                        color="success"
                                        disabled={!extension}
                                        onClick={handleContinueProcess}
                                        sx={{ mr: 1 }}
                                        variant={'contained'}
                                    >
                                        {extension === 'json' ||
                                        extension === 'zip'
                                            ? 'Start Import'
                                            : 'Continue to Mapping'}
                                    </Button>
                                )}
                            {activeStep !== 0 && (
                                <Button
                                    onClick={
                                        activeStep === steps.length - 1
                                            ? (e) => handleStartImport(e)
                                            : handleNext
                                    }
                                    variant={'contained'}
                                    color={'primary'}
                                    // @ts-ignore
                                    disabled={
                                        activeStep === 1 &&
                                        fileType === 'CASE' && // @ts-ignore
                                        (mappedFields.case_id === null ||
                                            // @ts-ignore
                                            (!mappedFields.case_id &&
                                                // @ts-ignore
                                                mappedFields.case_id !== 0))
                                    }
                                >
                                    {activeStep === steps.length - 1
                                        ? 'Start Import'
                                        : 'Next'}
                                </Button>
                            )}
                        </Box>
                    </React.Fragment>
                )}
            </Box>
        </Dialog>
    );
}
